import { apiFetch } from "../lib/api";
import { useState, useEffect, useCallback } from "react";
import { useOutletContext } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  Share2, Plus, Calendar, FileText, LayoutTemplate, Link2, MessageSquare,
  Settings, BarChart3, Send, Clock, CheckCircle2, XCircle, AlertTriangle,
  Edit, Trash2, Eye, MoreHorizontal, Sparkles, Loader2, ChevronLeft,
  ChevronRight, RefreshCw, ThumbsUp, ThumbsDown, RotateCcw, Filter,
  Instagram, Star, Megaphone,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

const API = process.env.REACT_APP_BACKEND_URL;

const TABS = [
  { id: "overview", label: "Resumen", icon: BarChart3 },
  { id: "calendar", label: "Calendario", icon: Calendar },
  { id: "posts", label: "Publicaciones", icon: FileText },
  { id: "analytics", label: "Analytics", icon: BarChart3 },
  { id: "templates", label: "Plantillas", icon: LayoutTemplate },
  { id: "accounts", label: "Cuentas", icon: Link2 },
  { id: "reviews", label: "Resenas", icon: MessageSquare },
  { id: "config", label: "Configuracion", icon: Settings },
];

const STATUS_CONFIG = {
  draft: { label: "Borrador", color: "bg-muted text-foreground", icon: Edit },
  pending_approval: { label: "Pendiente", color: "bg-amber-100 text-amber-700", icon: Clock },
  approved: { label: "Aprobado", color: "bg-blue-100 text-blue-700", icon: CheckCircle2 },
  scheduled: { label: "Programado", color: "bg-indigo-100 text-indigo-700", icon: Calendar },
  publishing: { label: "Publicando", color: "bg-cyan-100 text-cyan-700", icon: Loader2 },
  published: { label: "Publicado", color: "bg-emerald-100 text-emerald-700", icon: CheckCircle2 },
  partially_published: { label: "Parcial", color: "bg-orange-100 text-orange-700", icon: AlertTriangle },
  failed: { label: "Error", color: "bg-red-100 text-red-700", icon: XCircle },
  cancelled: { label: "Cancelado", color: "bg-muted text-muted-foreground", icon: XCircle },
};

const PLATFORM_ICONS = {
  google_business: Megaphone,
  instagram: Instagram,
  tiktok: Star,
};

const PLATFORM_LABELS = {
  google_business: "Google Business",
  instagram: "Instagram",
  tiktok: "TikTok",
};

const TEMPLATE_CATS = {
  menu_del_dia: "Menu del Dia",
  promocion: "Promocion",
  evento: "Evento",
  horario: "Horario Especial",
  resena: "Resena Destacada",
  plato: "Plato Recomendado",
};

// ── StatusBadge ───────────────────────────────────────────────────────
function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`} data-testid={`status-badge-${status}`}>
      <Icon className="w-3 h-3" /> {cfg.label}
    </span>
  );
}

// ── PlatformBadge ─────────────────────────────────────────────────────
function PlatformBadge({ platform }) {
  const Icon = PLATFORM_ICONS[platform] || Share2;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-muted text-muted-foreground">
      <Icon className="w-3 h-3" /> {PLATFORM_LABELS[platform] || platform}
    </span>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  OVERVIEW TAB
// ═══════════════════════════════════════════════════════════════════════
function OverviewTab({ rid, stats, loading }) {
  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;
  const kpis = [
    { label: "Cuentas conectadas", value: stats.connected_accounts, icon: Link2, color: "text-blue-600" },
    { label: "Borradores", value: stats.drafts, icon: Edit, color: "text-muted-foreground" },
    { label: "Pendientes", value: stats.pending_approval, icon: Clock, color: "text-amber-700" },
    { label: "Programadas", value: stats.scheduled, icon: Calendar, color: "text-indigo-600" },
    { label: "Publicadas", value: stats.published, icon: CheckCircle2, color: "text-emerald-700" },
    { label: "Errores", value: stats.failed, icon: XCircle, color: "text-red-700" },
  ];
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
        {kpis.map(k => (
          <Card key={k.label} className="border shadow-sm" data-testid={`kpi-${k.label.toLowerCase().replace(/ /g, "-")}`}>
            <CardContent className="p-4 flex flex-col items-center text-center gap-1">
              <k.icon className={`w-5 h-5 ${k.color}`} />
              <span className="text-2xl font-bold">{k.value}</span>
              <span className="text-xs text-muted-foreground">{k.label}</span>
            </CardContent>
          </Card>
        ))}
      </div>
      {stats.recent_errors?.length > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-2"><CardTitle className="text-sm text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Errores recientes</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {stats.recent_errors.map((e, i) => (
              <div key={`${e.post_id}-${e.platform}-${i}`} className="text-sm flex items-center gap-2">
                <PlatformBadge platform={e.platform} />
                <span className="text-muted-foreground">{e.error_message}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  CALENDAR TAB
// ═══════════════════════════════════════════════════════════════════════
function CalendarTab({ rid, posts }) {
  const [month, setMonth] = useState(() => {
    const d = new Date(); return { year: d.getFullYear(), month: d.getMonth() };
  });
  const firstDay = new Date(month.year, month.month, 1);
  const daysInMonth = new Date(month.year, month.month + 1, 0).getDate();
  const startDay = firstDay.getDay();
  const monthName = firstDay.toLocaleDateString("es-ES", { month: "long", year: "numeric" });

  const postsByDay = {};
  posts.forEach(p => {
    if (p.scheduled_for) {
      const d = new Date(p.scheduled_for).getDate();
      const m = new Date(p.scheduled_for).getMonth();
      const y = new Date(p.scheduled_for).getFullYear();
      if (y === month.year && m === month.month) {
        if (!postsByDay[d]) postsByDay[d] = [];
        postsByDay[d].push(p);
      }
    }
  });

  const prevMonth = () => setMonth(p => p.month === 0 ? { year: p.year - 1, month: 11 } : { ...p, month: p.month - 1 });
  const nextMonth = () => setMonth(p => p.month === 11 ? { year: p.year + 1, month: 0 } : { ...p, month: p.month + 1 });

  const cells = [];
  for (let i = 0; i < startDay; i++) cells.push(<div key={`e${i}`} className="h-20 border border-border/40 bg-muted/20 rounded" />);
  for (let d = 1; d <= daysInMonth; d++) {
    const dayPosts = postsByDay[d] || [];
    const isToday = d === new Date().getDate() && month.month === new Date().getMonth() && month.year === new Date().getFullYear();
    cells.push(
      <div key={d} className={`h-20 border rounded p-1 overflow-hidden ${isToday ? "border-primary bg-primary/5" : "border-border/40"}`}>
        <span className={`text-xs font-medium ${isToday ? "text-primary" : "text-muted-foreground"}`}>{d}</span>
        <div className="space-y-0.5 mt-0.5">
          {dayPosts.slice(0, 2).map(p => (
            <div key={p.post_id} className="text-[10px] leading-tight truncate px-1 py-0.5 rounded bg-primary/10 text-primary">
              {p.title || p.post_copy?.substring(0, 20)}
            </div>
          ))}
          {dayPosts.length > 2 && <span className="text-[10px] text-muted-foreground">+{dayPosts.length - 2} mas</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={prevMonth}><ChevronLeft className="w-4 h-4" /></Button>
        <h3 className="text-lg font-semibold capitalize">{monthName}</h3>
        <Button variant="ghost" size="sm" onClick={nextMonth}><ChevronRight className="w-4 h-4" /></Button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-center">
        {["Dom", "Lun", "Mar", "Mie", "Jue", "Vie", "Sab"].map(d => (
          <div key={d} className="text-xs font-medium text-muted-foreground py-1">{d}</div>
        ))}
        {cells}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  POSTS TAB
// ═══════════════════════════════════════════════════════════════════════
function PostsTab({ rid, posts, loading, onRefresh, onCreatePost, role }) {
  const [filter, setFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [approvalDialog, setApprovalDialog] = useState(null);
  const [comment, setComment] = useState("");

  const filtered = filter === "all" ? posts : posts.filter(p => p.status === filter);

  const handleAction = async (postId, action, body = {}) => {
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/posts/${postId}/${action}`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(body),
      }).then(async r => { if (!r.ok) { const e = await r.json(); throw new Error(e.detail); } return r.json(); });
      toast.success(`Accion completada`);
      onRefresh();
      setApprovalDialog(null);
      setComment("");
    } catch (e) { toast.error(e.message); }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm("Eliminar esta publicacion?")) return;
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/posts/${postId}`, {
        method: "DELETE", credentials: "include",
      });
      toast.success("Publicacion eliminada");
      onRefresh();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={onCreatePost} data-testid="create-post-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva publicacion
        </Button>
        <div className="flex flex-wrap gap-1 ml-auto">
          {[{ v: "all", l: "Todas" }, { v: "draft", l: "Borradores" }, { v: "pending_approval", l: "Pendientes" },
            { v: "scheduled", l: "Programadas" }, { v: "published", l: "Publicadas" }, { v: "failed", l: "Errores" }].map(f => (
            <Button key={f.v} variant={filter === f.v ? "default" : "outline"} size="sm" onClick={() => setFilter(f.v)}>
              {f.l}
            </Button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center text-muted-foreground">
            <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No hay publicaciones{filter !== "all" ? ` en estado "${STATUS_CONFIG[filter]?.label}"` : ""}</p>
            <p className="text-sm mt-1">Crea tu primera publicacion para empezar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map(post => (
            <Card key={post.post_id} className="hover:shadow-md transition-shadow" data-testid={`post-card-${post.post_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1 flex-wrap">
                      <StatusBadge status={post.status} />
                      {post.channels?.map(ch => <PlatformBadge key={ch} platform={ch} />)}
                      {post.ai_generated && <Badge variant="secondary" className="text-xs"><Sparkles className="w-3 h-3 mr-1" /> IA</Badge>}
                    </div>
                    <h4 className="font-medium text-sm truncate">{post.title || "(Sin titulo)"}</h4>
                    <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{post.post_copy}</p>
                    <div className="flex items-center gap-3 mt-2 text-[11px] text-muted-foreground">
                      {post.scheduled_for && (
                        <span className="flex items-center gap-1"><Calendar className="w-3 h-3" />{new Date(post.scheduled_for).toLocaleDateString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                      )}
                      {post.tags?.length > 0 && post.tags.map(t => <span key={t} className="bg-muted px-1.5 py-0.5 rounded">#{t}</span>)}
                    </div>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button variant="ghost" size="sm"><MoreHorizontal className="w-4 h-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => setDetail(post)}><Eye className="w-4 h-4 mr-2" /> Ver detalle</DropdownMenuItem>
                      {post.status === "draft" && (
                        <DropdownMenuItem onClick={() => handleAction(post.post_id, "submit")}><Send className="w-4 h-4 mr-2" /> Enviar a aprobacion</DropdownMenuItem>
                      )}
                      {post.status === "pending_approval" && role === "owner" && (
                        <DropdownMenuItem onClick={() => setApprovalDialog(post)}><ThumbsUp className="w-4 h-4 mr-2" /> Revisar</DropdownMenuItem>
                      )}
                      {(post.status === "approved" || post.status === "draft") && post.scheduled_for && (
                        <DropdownMenuItem onClick={() => handleAction(post.post_id, "schedule")}><Calendar className="w-4 h-4 mr-2" /> Programar</DropdownMenuItem>
                      )}
                      {role === "owner" && !["publishing", "published"].includes(post.status) && (
                        <DropdownMenuItem onClick={() => handleAction(post.post_id, "publish-now")}><Send className="w-4 h-4 mr-2" /> Publicar ahora</DropdownMenuItem>
                      )}
                      {["scheduled", "approved", "pending_approval"].includes(post.status) && (
                        <DropdownMenuItem onClick={() => handleAction(post.post_id, "cancel")}><XCircle className="w-4 h-4 mr-2" /> Cancelar</DropdownMenuItem>
                      )}
                      {["failed", "partially_published"].includes(post.status) && role === "owner" && (
                        <DropdownMenuItem onClick={() => handleAction(post.post_id, "retry")}><RefreshCw className="w-4 h-4 mr-2" /> Reintentar</DropdownMenuItem>
                      )}
                      {!["publishing", "published"].includes(post.status) && (
                        <DropdownMenuItem className="text-destructive" onClick={() => handleDelete(post.post_id)}><Trash2 className="w-4 h-4 mr-2" /> Eliminar</DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Approval Dialog */}
      <Dialog open={!!approvalDialog} onOpenChange={() => { setApprovalDialog(null); setComment(""); }}>
        <DialogContent>
          <DialogHeader><DialogTitle>Revisar publicacion</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm font-medium">{approvalDialog?.title}</p>
            <p className="text-sm text-muted-foreground">{approvalDialog?.post_copy}</p>
            <Textarea placeholder="Comentario (opcional)" value={comment} onChange={e => setComment(e.target.value)} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { handleAction(approvalDialog.post_id, "approve", { action: "return", comment }); }}>
              <RotateCcw className="w-4 h-4 mr-1" /> Devolver
            </Button>
            <Button variant="destructive" onClick={() => { handleAction(approvalDialog.post_id, "approve", { action: "reject", comment }); }}>
              <ThumbsDown className="w-4 h-4 mr-1" /> Rechazar
            </Button>
            <Button onClick={() => { handleAction(approvalDialog.post_id, "approve", { action: "approve", comment }); }} data-testid="approve-post-btn">
              <ThumbsUp className="w-4 h-4 mr-1" /> Aprobar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail Dialog */}
      <Dialog open={!!detail} onOpenChange={() => setDetail(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{detail?.title || "Detalle de publicacion"}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex items-center gap-2 flex-wrap">
                <StatusBadge status={detail.status} />
                {detail.channels?.map(ch => <PlatformBadge key={ch} platform={ch} />)}
              </div>
              <p className="whitespace-pre-wrap">{detail.post_copy}</p>
              {detail.image_url && <img src={detail.image_url} alt={`Imagen del post en ${detail.channel}`} className="rounded-lg max-h-48 object-cover" />}
              {detail.cta_text && <p className="text-xs"><strong>CTA:</strong> {detail.cta_text} {detail.cta_url && `→ ${detail.cta_url}`}</p>}
              {detail.scheduled_for && <p className="text-xs text-muted-foreground">Programado: {new Date(detail.scheduled_for).toLocaleString("es-ES")}</p>}
              {detail.published_at && <p className="text-xs text-emerald-700">Publicado: {new Date(detail.published_at).toLocaleString("es-ES")}</p>}
              {detail.tags?.length > 0 && <div className="flex gap-1 flex-wrap">{detail.tags.map(t => <Badge key={t} variant="outline" className="text-xs">#{t}</Badge>)}</div>}
              <p className="text-[11px] text-muted-foreground">Creado por {detail.created_by_name} el {new Date(detail.created_at).toLocaleString("es-ES")}</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  CREATE/EDIT POST DIALOG
// ═══════════════════════════════════════════════════════════════════════
function CreatePostDialog({ open, onClose, rid, accounts, onCreated, templates }) {
  const [form, setForm] = useState({ title: "", post_copy: "", channels: [], scheduled_for: "", tags: "", cta_text: "", cta_url: "", image_url: "" });
  const [saving, setSaving] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  const upd = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const toggleChannel = (ch) => setForm(p => ({
    ...p, channels: p.channels.includes(ch) ? p.channels.filter(c => c !== ch) : [...p.channels, ch]
  }));

  const handleSave = async () => {
    if (!form.post_copy.trim()) { toast.error("Escribe el texto de la publicacion"); return; }
    setSaving(true);
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/posts`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) }),
      }).then(async r => { if (!r.ok) { const e = await r.json(); throw new Error(e.detail); } return r.json(); });
      toast.success("Publicacion creada como borrador");
      setForm({ title: "", copy: "", channels: [], scheduled_for: "", tags: "", cta_text: "", cta_url: "", image_url: "" });
      onCreated();
      onClose();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const applyTemplate = (t) => {
    upd("copy", t.copy_template);
    upd("tags", t.tags?.join(", ") || "");
    toast.success(`Plantilla "${t.name}" aplicada`);
  };

  const applyAiCopy = (variant) => {
    upd("post_copy", variant.copy + (variant.hashtags?.length ? "\n\n" + variant.hashtags.join(" ") : ""));
    upd("cta_text", variant.cta || "");
    setAiOpen(false);
    toast.success("Copy IA aplicado");
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Nueva publicacion</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Input placeholder="Titulo (opcional)" value={form.title} onChange={e => upd("title", e.target.value)} data-testid="post-title-input" />

          {/* Channel selection */}
          <div>
            <label className="text-sm font-medium mb-1 block">Canales</label>
            <div className="flex gap-2 flex-wrap">
              {accounts.map(a => {
                const Icon = PLATFORM_ICONS[a.platform] || Share2;
                const sel = form.channels.includes(a.platform);
                return (
                  <Button key={a.account_id} variant={sel ? "default" : "outline"} size="sm" onClick={() => toggleChannel(a.platform)} data-testid={`channel-toggle-${a.platform}`}>
                    <Icon className="w-4 h-4 mr-1" /> {a.account_name}
                  </Button>
                );
              })}
              {accounts.length === 0 && <p className="text-sm text-muted-foreground">Conecta una cuenta primero en la pestana "Cuentas"</p>}
            </div>
          </div>

          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setAiOpen(!aiOpen)} data-testid="ai-generate-btn">
              <Sparkles className="w-4 h-4 mr-1" /> Generar con IA
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm"><LayoutTemplate className="w-4 h-4 mr-1" /> Plantilla</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="max-h-60 overflow-y-auto">
                {templates.map(t => (
                  <DropdownMenuItem key={t.template_id} onClick={() => applyTemplate(t)}>{t.name}</DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {aiOpen && <AIGeneratePanel rid={rid} onSelect={applyAiCopy} />}

          <Textarea placeholder="Escribe el copy de tu publicacion..." value={form.post_copy} onChange={e => upd("post_copy", e.target.value)} rows={5} data-testid="post-copy-input" />
          <Input placeholder="URL de imagen (opcional)" value={form.image_url} onChange={e => upd("image_url", e.target.value)} />
          <div className="grid grid-cols-2 gap-3">
            <Input placeholder="CTA texto" value={form.cta_text} onChange={e => upd("cta_text", e.target.value)} />
            <Input placeholder="CTA enlace" value={form.cta_url} onChange={e => upd("cta_url", e.target.value)} />
          </div>
          <Input type="datetime-local" value={form.scheduled_for} onChange={e => upd("scheduled_for", e.target.value ? new Date(e.target.value).toISOString() : "")} data-testid="post-schedule-input" />
          <Input placeholder="Etiquetas (separadas por coma)" value={form.tags} onChange={e => upd("tags", e.target.value)} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-post-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FileText className="w-4 h-4 mr-1" />} Guardar borrador
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  AI GENERATE PANEL (inline)
// ═══════════════════════════════════════════════════════════════════════
function AIGeneratePanel({ rid, onSelect }) {
  const [form, setForm] = useState({ objective: "", promotion_or_event: "", brand_tone: "cercano y comercial", language: "es" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const generate = async () => {
    if (!form.objective.trim()) { toast.error("Describe el objetivo del post"); return; }
    setLoading(true);
    try {
      const res = await apiFetch(`${API}/api/restaurants/${rid}/social/ai/generate-copy`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      });
      if (!res.ok) { const e = await res.json(); throw new Error(e.detail); }
      setResult(await res.json());
    } catch (e) { toast.error(e.message); } finally { setLoading(false); }
  };

  return (
    <Card className="border-primary/30 bg-primary/5">
      <CardContent className="p-4 space-y-3">
        <h4 className="text-sm font-semibold flex items-center gap-1"><Sparkles className="w-4 h-4 text-primary" /> Generador IA</h4>
        <div className="grid grid-cols-2 gap-2">
          <Input placeholder="Objetivo del post (ej: promocionar menu del dia)" value={form.objective} onChange={e => setForm(p => ({ ...p, objective: e.target.value }))} data-testid="ai-objective-input" />
          <Input placeholder="Promocion o evento (opcional)" value={form.promotion_or_event} onChange={e => setForm(p => ({ ...p, promotion_or_event: e.target.value }))} />
        </div>
        <div className="flex gap-2 items-center">
          <Select value={form.brand_tone} onValueChange={v => setForm(p => ({ ...p, brand_tone: v }))}>
            <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="cercano y comercial">Cercano</SelectItem>
              <SelectItem value="formal y elegante">Elegante</SelectItem>
              <SelectItem value="divertido y juvenil">Divertido</SelectItem>
              <SelectItem value="minimalista y directo">Minimalista</SelectItem>
            </SelectContent>
          </Select>
          <Select value={form.language} onValueChange={v => setForm(p => ({ ...p, language: v }))}>
            <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="es">Espanol</SelectItem>
              <SelectItem value="en">English</SelectItem>
              <SelectItem value="ca">Catala</SelectItem>
              <SelectItem value="eu">Euskara</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={generate} disabled={loading} size="sm" data-testid="ai-generate-copy-btn">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1" />} Generar
          </Button>
        </div>
        {result?.variants && (
          <div className="space-y-2 mt-2">
            {result.variants.map((v, i) => (
              <div key={`variant-${i}`} className="bg-background rounded-lg p-3 border text-sm">
                <p className="whitespace-pre-wrap">{v.copy}</p>
                {v.hashtags?.length > 0 && <p className="text-xs text-primary mt-1">{v.hashtags.join(" ")}</p>}
                {v.cta && <p className="text-xs text-muted-foreground mt-1">CTA: {v.cta}</p>}
                <Button variant="outline" size="sm" className="mt-2" onClick={() => onSelect(v)}>Usar esta variante</Button>
              </div>
            ))}
            {result.visual_idea && (
              <p className="text-xs text-muted-foreground border-t pt-2">Idea visual: {result.visual_idea}</p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  TEMPLATES TAB
// ═══════════════════════════════════════════════════════════════════════
function TemplatesTab({ rid, templates, loading, onRefresh }) {
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", category: "menu_del_dia", copy_template: "", tags: "" });
  const [saving, setSaving] = useState(false);

  const handleCreate = async () => {
    if (!form.name.trim() || !form.copy_template.trim()) { toast.error("Nombre y texto son obligatorios"); return; }
    setSaving(true);
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/templates`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ ...form, tags: form.tags.split(",").map(t => t.trim()).filter(Boolean) }),
      }).then(async r => { if (!r.ok) throw new Error((await r.json()).detail); });
      toast.success("Plantilla creada");
      setForm({ name: "", category: "menu_del_dia", copy_template: "", tags: "" });
      setShowCreate(false);
      onRefresh();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleDelete = async (tid) => {
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/templates/${tid}`, { method: "DELETE", credentials: "include" });
      toast.success("Plantilla eliminada");
      onRefresh();
    } catch { toast.error("Error al eliminar"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{templates.length} plantillas disponibles</p>
        <Button size="sm" onClick={() => setShowCreate(true)} data-testid="create-template-btn"><Plus className="w-4 h-4 mr-1" /> Nueva plantilla</Button>
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {templates.map(t => (
            <Card key={t.template_id} className="hover:shadow-md transition-shadow" data-testid={`template-card-${t.template_id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="text-xs mb-2">{TEMPLATE_CATS[t.category] || t.category}</Badge>
                    <h4 className="font-medium text-sm">{t.name}</h4>
                  </div>
                  {t.restaurant_id !== "__global__" && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(t.template_id)}><Trash2 className="w-3.5 h-3.5 text-muted-foreground" /></Button>
                  )}
                </div>
                <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{t.copy_template}</p>
                {t.tags?.length > 0 && <div className="flex gap-1 mt-2 flex-wrap">{t.tags.map(tg => <span key={tg} className="text-[10px] bg-muted px-1.5 py-0.5 rounded">#{tg}</span>)}</div>}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nueva plantilla</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="Nombre de la plantilla" value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            <Select value={form.category} onValueChange={v => setForm(p => ({ ...p, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(TEMPLATE_CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Textarea placeholder="Texto de la plantilla (usa {variable} para campos dinamicos)" value={form.copy_template} onChange={e => setForm(p => ({ ...p, copy_template: e.target.value }))} rows={4} />
            <Input placeholder="Etiquetas (separadas por coma)" value={form.tags} onChange={e => setForm(p => ({ ...p, tags: e.target.value }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancelar</Button>
            <Button onClick={handleCreate} disabled={saving}>{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Crear"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  ACCOUNTS TAB
// ═══════════════════════════════════════════════════════════════════════
function AccountsTab({ rid, accounts, loading, onRefresh, role }) {
  const [showConnect, setShowConnect] = useState(false);
  const [form, setForm] = useState({ platform: "", account_name: "" });
  const [saving, setSaving] = useState(false);
  const [oauthStatus, setOauthStatus] = useState({});

  useEffect(() => {
    apiFetch(`${API}/api/restaurants/${rid}/social/oauth/status`, { credentials: "include" })
      .then(r => r.json()).then(setOauthStatus).catch(() => {});
  }, [rid, accounts.length]);

  const handleConnect = async () => {
    if (!form.platform || !form.account_name.trim()) { toast.error("Selecciona plataforma y nombre de cuenta"); return; }
    setSaving(true);
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/accounts`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(form),
      }).then(async r => { if (!r.ok) throw new Error((await r.json()).detail); });
      toast.success("Cuenta conectada");
      setForm({ platform: "", account_name: "" });
      setShowConnect(false);
      onRefresh();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  const handleOAuthConnect = async (platform) => {
    try {
      const res = await apiFetch(`${API}/api/restaurants/${rid}/social/oauth/${platform}/authorize`, { credentials: "include" });
      if (!res.ok) { const e = await res.json(); toast.error(e.detail); return; }
      const data = await res.json();
      window.location.href = data.auth_url;
    } catch (e) { toast.error("Error al iniciar OAuth: " + e.message); }
  };

  const handleDisconnect = async (accountId) => {
    if (!window.confirm("Desconectar esta cuenta?")) return;
    try {
      await apiFetch(`${API}/api/restaurants/${rid}/social/accounts/${accountId}`, { method: "DELETE", credentials: "include" });
      toast.success("Cuenta desconectada");
      onRefresh();
    } catch { toast.error("Error"); }
  };

  const availablePlatforms = Object.entries(PLATFORM_LABELS).filter(([k]) => !accounts.find(a => a.platform === k));

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <p className="text-sm text-muted-foreground">{accounts.length} cuentas conectadas</p>
        {role === "owner" && availablePlatforms.length > 0 && (
          <Button size="sm" onClick={() => setShowConnect(true)} data-testid="connect-account-btn"><Plus className="w-4 h-4 mr-1" /> Conectar cuenta</Button>
        )}
      </div>
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>
      ) : accounts.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="p-12 text-center text-muted-foreground">
            <Link2 className="w-10 h-10 mx-auto mb-3 opacity-40" />
            <p className="font-medium">No hay cuentas conectadas</p>
            <p className="text-sm mt-1">Conecta tus redes sociales para empezar a publicar</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(a => {
            const Icon = PLATFORM_ICONS[a.platform] || Share2;
            const oauth = oauthStatus[a.platform];
            return (
              <Card key={a.account_id} data-testid={`account-card-${a.account_id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-medium text-sm truncate">{a.account_name}</h4>
                      <p className="text-xs text-muted-foreground">{PLATFORM_LABELS[a.platform]}</p>
                      <div className="flex gap-1 mt-1 flex-wrap">
                        <Badge variant={a.status === "connected" ? "default" : "secondary"} className="text-[10px]">
                          {a.status === "connected" ? "Conectada" : a.status}
                        </Badge>
                        {oauth?.has_tokens && (
                          <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700">OAuth activo</Badge>
                        )}
                        {oauth?.oauth_supported && !oauth?.has_tokens && oauth?.oauth_configured && (
                          <Button variant="outline" size="sm" className="h-5 text-[10px] px-2" onClick={() => handleOAuthConnect(a.platform)}>
                            Vincular OAuth
                          </Button>
                        )}
                      </div>
                    </div>
                    {role === "owner" && (
                      <Button variant="ghost" size="sm" onClick={() => handleDisconnect(a.account_id)}><Trash2 className="w-4 h-4 text-muted-foreground" /></Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
      <Dialog open={showConnect} onOpenChange={setShowConnect}>
        <DialogContent>
          <DialogHeader><DialogTitle>Conectar cuenta de red social</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.platform} onValueChange={v => setForm(p => ({ ...p, platform: v }))}>
              <SelectTrigger><SelectValue placeholder="Selecciona plataforma" /></SelectTrigger>
              <SelectContent>
                {availablePlatforms.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input placeholder="Nombre de la cuenta (ej: @mirestaurante)" value={form.account_name} onChange={e => setForm(p => ({ ...p, account_name: e.target.value }))} data-testid="account-name-input" />
            {form.platform && oauthStatus[form.platform]?.oauth_configured && (
              <div className="bg-emerald-50 text-emerald-700 text-xs p-3 rounded-lg">
                OAuth disponible para {PLATFORM_LABELS[form.platform]}. Despues de conectar la cuenta podras vincular OAuth para publicar automaticamente.
              </div>
            )}
            {form.platform && !oauthStatus[form.platform]?.oauth_configured && oauthStatus[form.platform]?.oauth_supported && (
              <div className="bg-amber-50 text-amber-700 text-xs p-3 rounded-lg">
                OAuth no configurado para {PLATFORM_LABELS[form.platform]}. Conecta la cuenta manualmente. Cuando el admin configure las credenciales OAuth, podras vincularla.
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConnect(false)}>Cancelar</Button>
            <Button onClick={handleConnect} disabled={saving} data-testid="save-account-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Conectar"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  ANALYTICS TAB
// ═══════════════════════════════════════════════════════════════════════
function AnalyticsTab({ rid }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  useEffect(() => {
    setLoading(true);
    apiFetch(`${API}/api/restaurants/${rid}/social/analytics?days=${days}`, { credentials: "include" })
      .then(r => r.json()).then(setData).catch(() => {}).finally(() => setLoading(false));
  }, [rid, days]);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  if (!data) return <p className="text-muted-foreground text-sm text-center py-8">No hay datos disponibles</p>;

  const maxTimeline = Math.max(...(data.timeline?.map(t => t.count) || [1]), 1);

  return (
    <div className="space-y-6" data-testid="analytics-tab">
      {/* Period selector */}
      <div className="flex gap-2 items-center">
        <span className="text-sm text-muted-foreground">Periodo:</span>
        {[7, 30, 90].map(d => (
          <Button key={d} variant={days === d ? "default" : "outline"} size="sm" onClick={() => setDays(d)}>
            {d === 7 ? "7 dias" : d === 30 ? "30 dias" : "90 dias"}
          </Button>
        ))}
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total publicaciones", value: data.total_posts, color: "text-foreground" },
          { label: "Publicadas", value: data.published_count, color: "text-emerald-700" },
          { label: "Tasa aprobacion", value: `${data.approval_rate}%`, color: "text-blue-600" },
          { label: "Tiempo medio aprobacion", value: `${data.avg_approval_hours}h`, color: "text-amber-700" },
        ].map(k => (
          <Card key={k.label}>
            <CardContent className="p-4 text-center">
              <span className={`text-2xl font-bold ${k.color}`}>{k.value}</span>
              <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Timeline chart */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Publicaciones por dia</CardTitle></CardHeader>
          <CardContent>
            {data.timeline?.length > 0 ? (
              <div className="flex items-end gap-1 h-32">
                {data.timeline.map(t => (
                  <div key={t.date} className="flex-1 flex flex-col items-center gap-1">
                    <div
                      className="w-full bg-primary/80 rounded-t-sm min-h-[2px] transition-all"
                      style={{ height: `${(t.count / maxTimeline) * 100}%` }}
                      title={`${t.date}: ${t.count}`}
                    />
                    <span className="text-[8px] text-muted-foreground -rotate-45 origin-top-left whitespace-nowrap">
                      {t.date.slice(5)}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground text-center py-8">Sin datos en este periodo</p>
            )}
          </CardContent>
        </Card>

        {/* Channel distribution */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Rendimiento por canal</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {Object.entries(data.by_channel || {}).map(([platform, stats]) => {
              const Icon = PLATFORM_ICONS[platform] || Share2;
              const total = stats.total || 1;
              const pubPct = Math.round((stats.published / total) * 100);
              const errPct = Math.round((stats.errors / total) * 100);
              return (
                <div key={platform} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-sm flex items-center gap-1"><Icon className="w-3.5 h-3.5" /> {PLATFORM_LABELS[platform]}</span>
                    <span className="text-xs text-muted-foreground">{stats.published}/{stats.total} publicados</span>
                  </div>
                  <div className="w-full h-2 bg-muted rounded-full overflow-hidden flex">
                    <div className="bg-emerald-500 h-full transition-all" style={{ width: `${pubPct}%` }} />
                    <div className="bg-red-400 h-full transition-all" style={{ width: `${errPct}%` }} />
                  </div>
                </div>
              );
            })}
            {Object.keys(data.by_channel || {}).length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">Sin datos por canal</p>
            )}
          </CardContent>
        </Card>

        {/* By status */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Distribucion por estado</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(data.by_status || {}).map(([status, count]) => (
              <div key={status} className="flex items-center justify-between">
                <StatusBadge status={status} />
                <span className="text-sm font-medium">{count}</span>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Best times + Tags */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-sm">Mejores momentos y etiquetas</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            {data.best_hours?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Mejores horas</p>
                <div className="flex gap-2 flex-wrap">
                  {data.best_hours.map(h => (
                    <Badge key={h.hour} variant="outline" className="text-xs">{h.hour}:00 ({h.count})</Badge>
                  ))}
                </div>
              </div>
            )}
            {data.best_days?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Mejores dias</p>
                <div className="flex gap-2 flex-wrap">
                  {data.best_days.map(d => (
                    <Badge key={d.day} variant="outline" className="text-xs">{d.day} ({d.count})</Badge>
                  ))}
                </div>
              </div>
            )}
            {data.top_tags?.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-1">Top etiquetas</p>
                <div className="flex gap-1 flex-wrap">
                  {data.top_tags.map(t => (
                    <span key={t.tag} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full">#{t.tag} ({t.count})</span>
                  ))}
                </div>
              </div>
            )}
            <div className="border-t pt-2">
              <p className="text-xs text-muted-foreground">IA: {data.ai_posts} posts generados, {data.ai_generations} generaciones totales</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* External insights notice */}
      {!data.external_insights_available && (
        <Card className="border-dashed">
          <CardContent className="p-6 text-center text-muted-foreground">
            <BarChart3 className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm font-medium">Metricas de engagement externas</p>
            <p className="text-xs mt-1">Cuando conectes tus cuentas via OAuth, aqui veras impresiones, alcance, engagement y clics de cada publicacion en tiempo real.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  REVIEWS TAB (PLACEHOLDER)
// ═══════════════════════════════════════════════════════════════════════
function ReviewsTab() {
  return (
    <Card className="border-dashed">
      <CardContent className="p-12 text-center text-muted-foreground">
        <MessageSquare className="w-12 h-12 mx-auto mb-4 opacity-30" />
        <h3 className="font-semibold text-lg mb-2">Gestion de Resenas</h3>
        <p className="text-sm max-w-md mx-auto">
          Proximamente podras ver las resenas de Google y responder con sugerencias de IA.
          La estructura esta preparada para esta funcionalidad.
        </p>
        <Badge variant="secondary" className="mt-4">Proximamente</Badge>
      </CardContent>
    </Card>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  CONFIG TAB
// ═══════════════════════════════════════════════════════════════════════
function ConfigTab({ rid, platforms }) {
  const [oauthStatus, setOauthStatus] = useState({});

  useEffect(() => {
    apiFetch(`${API}/api/restaurants/${rid}/social/oauth/status`, { credentials: "include" })
      .then(r => r.json()).then(setOauthStatus).catch(() => {});
  }, [rid]);

  return (
    <div className="space-y-6">
      <h3 className="font-semibold">Plataformas soportadas</h3>
      <div className="grid md:grid-cols-3 gap-4">
        {Object.entries(platforms).map(([key, caps]) => {
          const Icon = PLATFORM_ICONS[key] || Share2;
          const oauth = oauthStatus[key];
          return (
            <Card key={key}>
              <CardContent className="p-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center"><Icon className="w-5 h-5 text-primary" /></div>
                  <div>
                    <h4 className="font-medium">{caps.label}</h4>
                    {oauth?.oauth_supported && (
                      <Badge variant={oauth?.oauth_configured ? "default" : "secondary"} className="text-[10px] mt-0.5">
                        {oauth?.oauth_configured ? "OAuth configurado" : "OAuth pendiente"}
                      </Badge>
                    )}
                  </div>
                </div>
                <div className="space-y-1 text-xs text-muted-foreground">
                  <p>Imagenes: {caps.supports_image ? "Si" : "No"}</p>
                  <p>Video: {caps.supports_video ? "Si" : "No"}</p>
                  <p>CTA: {caps.supports_cta ? "Si" : "No"}</p>
                  <p>Hashtags: {caps.supports_hashtags ? "Si" : "No"}</p>
                  <p>Max caracteres: {caps.max_chars}</p>
                </div>
                {oauth?.oauth_supported && !oauth?.oauth_configured && (
                  <p className="text-[10px] text-amber-700 mt-2 bg-amber-50 p-2 rounded">
                    Para activar la publicacion automatica, configura las credenciales OAuth en el archivo .env del servidor.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════
//  MAIN PAGE
// ═══════════════════════════════════════════════════════════════════════
export default function SocialMediaPage() {
  const { currentRestaurant, currentRole } = useOutletContext();
  const rid = currentRestaurant?.restaurant_id;
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState({});
  const [posts, setPosts] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [platforms, setPlatforms] = useState({});
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!rid) return;
    setLoading(true);
    try {
      const [statsRes, postsRes, accsRes, tmplRes, platRes] = await Promise.all([
        apiFetch(`${API}/api/restaurants/${rid}/social/stats`, { credentials: "include" }).then(r => r.json()),
        apiFetch(`${API}/api/restaurants/${rid}/social/posts?limit=200`, { credentials: "include" }).then(r => r.json()),
        apiFetch(`${API}/api/restaurants/${rid}/social/accounts`, { credentials: "include" }).then(r => r.json()),
        apiFetch(`${API}/api/restaurants/${rid}/social/templates`, { credentials: "include" }).then(r => r.json()),
        apiFetch(`${API}/api/restaurants/${rid}/social/platforms`, { credentials: "include" }).then(r => r.json()),
      ]);
      setStats(statsRes);
      setPosts(postsRes.posts || []);
      setAccounts(accsRes || []);
      setTemplates(tmplRes.templates || []);
      setPlatforms(platRes || {});
    } catch (e) {
      console.error("Social fetch error:", e);
    } finally { setLoading(false); }
  }, [rid]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  if (!rid) return null;

  return (
    <div className="space-y-6" data-testid="social-media-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Share2 className="w-6 h-6 text-primary" /> Redes Sociales
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestiona tus publicaciones en redes sociales desde un unico lugar</p>
        </div>
        <Button onClick={() => { setTab("posts"); setShowCreate(true); }} data-testid="new-post-header-btn">
          <Plus className="w-4 h-4 mr-1" /> Nueva publicacion
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 overflow-x-auto pb-1 border-b" data-testid="social-tabs">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-t-lg whitespace-nowrap transition-colors ${
              tab === t.id
                ? "bg-primary/10 text-primary border-b-2 border-primary"
                : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
            }`}
            data-testid={`social-tab-${t.id}`}
          >
            <t.icon className="w-4 h-4" /> {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {tab === "overview" && <OverviewTab rid={rid} stats={stats} loading={loading} />}
      {tab === "calendar" && <CalendarTab rid={rid} posts={posts} />}
      {tab === "posts" && <PostsTab rid={rid} posts={posts} loading={loading} onRefresh={fetchAll} onCreatePost={() => setShowCreate(true)} role={currentRole} />}
      {tab === "analytics" && <AnalyticsTab rid={rid} />}
      {tab === "templates" && <TemplatesTab rid={rid} templates={templates} loading={loading} onRefresh={fetchAll} />}
      {tab === "accounts" && <AccountsTab rid={rid} accounts={accounts} loading={loading} onRefresh={fetchAll} role={currentRole} />}
      {tab === "reviews" && <ReviewsTab />}
      {tab === "config" && <ConfigTab rid={rid} platforms={platforms} />}

      {/* Create post dialog */}
      <CreatePostDialog open={showCreate} onClose={() => setShowCreate(false)} rid={rid} accounts={accounts} onCreated={fetchAll} templates={templates} />
    </div>
  );
}
